import axios from 'axios';
import { API_BASE_URL } from '../config';

// Configurar axios para admin (usa cookies)
const adminApi = axios.create({
  baseURL: `${API_BASE_URL}/api/admin`,
  withCredentials: true, // IMPORTANTE: siempre enviar cookies
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor para manejar errores de sesión expirada
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Sesión expirada o no válida
      console.warn('⚠️ Sesión de admin expirada');
      sessionStorage.removeItem('adminData');

      // Redirigir al login si no estamos ya ahí
      if (window.location.pathname !== '/admin-login') {
        window.location.href = '/admin-login';
      }
    }
    return Promise.reject(error);
  }
);

// ============================================
// AUTENTICACIÓN
// ============================================

export const adminLogin = async (correo, password) => {
  const response = await adminApi.post('/login', { correo, password });
  return response.data;
};

export const adminLogout = async () => {
  const response = await adminApi.post('/logout');
  sessionStorage.removeItem('adminData');
  return response.data;
};

export const getAdminMe = async () => {
  const response = await adminApi.get('/me');
  return response.data;
};

// ============================================
// GESTIÓN DE CLIENTES
// ============================================

export const getClientes = async () => {
  const response = await adminApi.get('/clientes');
  return response.data;
};

export const getCliente = async (id) => {
  const response = await adminApi.get(`/clientes/${id}`);
  return response.data;
};

export const createCliente = async (clienteData) => {
  const response = await adminApi.post('/clientes', clienteData);
  return response.data;
};

export const updateCliente = async (id, clienteData) => {
  const response = await adminApi.put(`/clientes/${id}`, clienteData);
  return response.data;
};

export const deleteCliente = async (id) => {
  const response = await adminApi.delete(`/clientes/${id}`);
  return response.data;
};

// ============================================
// GESTIÓN DE EMPRESAS
// ============================================

export const getCompaniesByCliente = async (idCliente) => {
  const response = await adminApi.get(`/clientes/${idCliente}/companies`);
  return response.data;
};

// Inactivar empresa (soft delete)
export const deactivateCompany = async (companyId) => {
  const response = await adminApi.patch(`/companies/${companyId}/deactivate`);
  return response.data;
};

// Reactivar empresa
export const reactivateCompany = async (companyId) => {
  const response = await adminApi.patch(`/companies/${companyId}/reactivate`);
  return response.data;
};

// Eliminar empresa permanentemente (hard delete)
export const deleteCompany = async (companyId) => {
  const response = await adminApi.delete(`/companies/${companyId}`);
  return response.data;
};

// Inactivar múltiples empresas (soft delete masivo)
export const bulkDeactivateCompanies = async (companyIds) => {
  const response = await adminApi.post('/companies/bulk-deactivate', { companyIds });
  return response.data;
};

// Eliminar múltiples empresas permanentemente (hard delete masivo)
export const bulkDeleteCompanies = async (companyIds) => {
  const response = await adminApi.post('/companies/bulk-delete', { companyIds });
  return response.data;
};

// ============================================
// GESTIÓN DE PAGOS
// ============================================

export const getPagos = async () => {
  const response = await adminApi.get('/pagos');
  return response.data;
};

export const getPagosPendientes = async () => {
  const response = await adminApi.get('/pagos/pendientes');
  return response.data;
};

export const getPagosByCliente = async (idCliente) => {
  const response = await adminApi.get(`/clientes/${idCliente}/pagos`);
  return response.data;
};

export const createPago = async (pagoData) => {
  const response = await adminApi.post('/pagos', pagoData);
  return response.data;
};

export const updatePago = async (id, pagoData) => {
  const response = await adminApi.put(`/pagos/${id}`, pagoData);
  return response.data;
};

export const markPagoAsPaid = async (id, pagoInfo) => {
  const response = await adminApi.put(`/pagos/${id}/marcar-pagado`, pagoInfo);
  return response.data;
};

export const deletePago = async (id) => {
  const response = await adminApi.delete(`/pagos/${id}`);
  return response.data;
};

// ============================================
// ESTADÍSTICAS
// ============================================

export const getStats = async () => {
  const response = await adminApi.get('/stats');
  return response.data;
};

// ============================================
// NOTIFICACIONES
// ============================================

export const getNotifLogs = async (params = {}) => {
  const response = await adminApi.get('/notificaciones/logs', { params });
  return response.data;
};

export const getNotifConfigs = async () => {
  const response = await adminApi.get('/notificaciones/config');
  return response.data;
};

export const createNotifConfig = async (data) => {
  const response = await adminApi.post('/notificaciones/config', data);
  return response.data;
};

export const updateNotifConfig = async (id, data) => {
  const response = await adminApi.put(`/notificaciones/config/${id}`, data);
  return response.data;
};

export const toggleNotifConfig = async (id, activo) => {
  const response = await adminApi.patch(`/notificaciones/config/${id}/toggle`, { activo });
  return response.data;
};

export const deleteNotifConfig = async (id) => {
  const response = await adminApi.delete(`/notificaciones/config/${id}`);
  return response.data;
};

export const enviarNotifAhora = async (id) => {
  const response = await adminApi.post(`/notificaciones/config/${id}/enviar-ahora`);
  return response.data;
};

export default adminApi;
